import i18nFactory from '@yandex-int/i18n';
import { get, some, toPath, uniq } from 'lodash';

import {
    ValidationErrorData,
    ValidationResultItem
} from '../../typings/common';
import { CampaignType } from '../../typings/domain';
import {
    GdGoalType,
    GdRetargetingConditionRuleType,
    GoalSubtype
} from '../../typings/graphql-grid';
import {
    RetargetingConditionDataRule,
    RetargetingConditionDataRulesGroup,
    RetargetingConditionGoal,
    RetargetingConditionRuleType
} from '../../typings/retargeting';
import { State } from '../../typings/state';
import { createErrorsMapper, FieldPathMap } from '../ErrorsMapper/ErrorsMapper';

import * as keyset from './RetargetingConditionEditorHelpers.i18n';

const i18n = i18nFactory(keyset);

const MEDIANA_SOURCE_ID = '7';

export function validateRetargetingConditionName(
    validationParams: { maxLength: number },
    value: string
): ValidationErrorData[] {
    const errors = [];
    const { maxLength } = validationParams;

    if (value === undefined || value === '') {
        errors.push({
            code: 'required',
            text: i18n('name-required')
        });
    } else {
        if (value.length > maxLength) {
            errors.push({
                code: 'name-max-length',
                text: i18n('name-max-length', { value: maxLength })
            });
        }
    }

    return errors;
}

export function validateRetargetingConditionDescription(
    validationParams: { maxLength: number },
    value: string
): ValidationErrorData[] {
    const errors = [];
    const { maxLength } = validationParams;

    if (value.length > maxLength) {
        errors.push({
            code: 'description-max-length',
            text: i18n('description-max-length', { value: maxLength })
        });
    }

    return errors;
}

export function validateRetargetingConditionRuleGoal(
    ruleValue: RetargetingConditionDataRule
): ValidationErrorData[] {
    const errors = [];

    if (ruleValue.id === undefined) {
        errors.push({
            code: 'condition-rule-id-required',
            text: i18n('condition-rule-id-required')
        });
    }

    return errors;
}

export function validateRetargetingConditionRulePeriod(
    validationParams: { periodMaxValue: number },
    ruleValue: RetargetingConditionDataRule
): ValidationErrorData[] {
    const errors: ValidationErrorData[] = [];
    const { periodMaxValue } = validationParams;
    const { period, type } = ruleValue;

    if (
        type === RetargetingConditionRuleType.AUDIENCE_SEGMENT_RULE ||
        type === RetargetingConditionRuleType.METRIKA_SEGMENT_RULE
    ) {
        return errors;
    }

    if (!period) {
        errors.push({
            code: 'condition-rule-period-required',
            text: i18n('condition-rule-period-required')
        });
    } else if (period < 0) {
        errors.push({
            code: 'condition-rule-period-value-negative-value',
            text: i18n('condition-rule-period-value-negative-value')
        });
    } else if (period > periodMaxValue) {
        errors.push({
            code: 'condition-rule-period-value-max-value',
            text: i18n('condition-rule-period-value-max-value', {
                value: periodMaxValue
            })
        });
    }

    return errors;
}

export function validateConditionsGroups(
    rulesGroups: Array<RetargetingConditionDataRulesGroup>,
    campaignType?: CampaignType
): ValidationErrorData[] {
    if (campaignType && campaignType === CampaignType.CPM_BANNER) {
        return [];
    }

    const errors = [];
    const areAllConditionsNegative = rulesGroups.every(
        group => group.type === GdRetargetingConditionRuleType.NOT
    );

    if (areAllConditionsNegative) {
        errors.push({
            code: 'all-conditions-are-negative',
            text: i18n('all-conditions-are-negative')
        });
    }

    return errors;
}

export const getSegmentOrGoalError = (
    rulesGroups: Array<RetargetingConditionDataRulesGroup>
): ValidationErrorData[] => {
    const newErrors = rulesGroups.reduce((acc: number[], group, index) => {
        const hasErrors = some(group.items, item => {
            return (
                (item.errors.id !== undefined && item.errors.id.length !== 0) ||
                item.id === undefined
            );
        });

        if (hasErrors) {
            acc.push(index + 1);
        }

        return acc;
    }, []);

    if (newErrors.length === 0) {
        return [];
    }

    const numberOfGroups = newErrors.join(', ');

    return [
        {
            text: i18n('goals-or-segment-error', { numberOfGroups })
        }
    ];
};

export const hasRetargetingConditionEditorValidationErrors = (
    state: State
): boolean => {
    const {
        retargetingConditionEditor: {
            conditionData,
            errors: { name = [], general = [], description = [] }
        }
    } = state;

    const hasRulesErrors = some(conditionData.rulesGroups, rulesGroup => {
        return some(rulesGroup.items, item =>
            some(item.errors, itemError => itemError && itemError.length)
        );
    });

    const hasGeneralErrors = [...general, ...name, ...description].length !== 0;

    return hasRulesErrors || hasGeneralErrors;
};

export const getRetargetingConditionRulesGroupType = (
    type: GdGoalType
): RetargetingConditionRuleType | undefined => {
    const dict = {
        [GdGoalType.ECOMMERCE]: RetargetingConditionRuleType.METRIKA_GOAL_RULE,
        [GdGoalType.GOAL]: RetargetingConditionRuleType.METRIKA_GOAL_RULE,
        [GdGoalType.AUDIENCE]:
            RetargetingConditionRuleType.AUDIENCE_SEGMENT_RULE,
        [GdGoalType.SEGMENT]: RetargetingConditionRuleType.METRIKA_SEGMENT_RULE
    };

    return dict[type];
};

export const retargetingConditionServerErrorMapper = function(
    index: string
): FieldPathMap[] {
    return [
        {
            serverPath: `retargetingConditions.${index}`,
            clientPath: `retargetingConditions.${index}.general`
        },
        {
            serverPath: `retargetingConditions.${index}.name`,
            clientPath: `retargetingConditions.${index}.name`
        },
        {
            serverPath: `retargetingConditions.${index}.description`,
            clientPath: `retargetingConditions.${index}.description`
        },
        {
            serverPath: `retargetingConditions.${index}.rules`,
            clientPath: `retargetingConditions.${index}.rulesGroupsGeneral`
        },
        {
            serverPath: `retargetingConditions.${index}.rules.${index}.goals`,
            clientPath: `retargetingConditions.${index}.rulesGroups.${index}.items`
        },
        {
            serverPath: `retargetingConditions.${index}.rules.${index}.goals.${index}.id`,
            clientPath: `retargetingConditions.${index}.rulesGroups.${index}.items.${index}.id`
        },
        {
            serverPath: `retargetingConditions.${index}.rules.${index}.goals.${index}.time`,
            clientPath: `retargetingConditions.${index}.rulesGroups.${index}.items.${index}.period`
        }
    ].sort((a, b) => toPath(b.serverPath).length - toPath(a.serverPath).length);
};

export const retargetingConditionServerErrorsTextMapper = createErrorsMapper<{}>(
    ({ idx }) => [
        {
            path: ['retargetingConditions', idx, 'general'],
            codes: [
                {
                    code:
                        'RetargetingDefectIds.Gen.CANNOT_CHANGE_RETARGETING_SCOPE',
                    message: (err: ValidationResultItem): string => {
                        return i18n('all-conditions-are-negative');
                    }
                }
            ]
        },
        {
            path: ['retargetingConditions', idx, 'name'],
            codes: [
                {
                    code: 'StringDefectIds.CANNOT_BE_EMPTY',
                    message: (err: ValidationResultItem): string => {
                        return i18n('name-required');
                    }
                },
                {
                    code: 'StringDefectIds.LENGTH_CANNOT_BE_MORE_THAN_MAX',
                    message: (err: ValidationResultItem): string => {
                        return i18n('name-max-length', {
                            value: get(err, 'params.maxLength', '')
                        });
                    }
                },
                {
                    code: 'StringDefectIds.MUST_CONTAIN_ONLY_SYMBOLS',
                    message: (err: ValidationResultItem): string => {
                        return i18n('name-must-contain-only-symbols');
                    }
                },
                {
                    code: 'DefectIds.INCONSISTENT_STATE_ALREADY_EXISTS',
                    message: (err: ValidationResultItem): string => {
                        return i18n('inconsistent-name');
                    }
                }
            ]
        },
        {
            path: ['retargetingConditions', idx, 'description'],
            codes: [
                {
                    code: 'StringDefectIds.LENGTH_CANNOT_BE_MORE_THAN_MAX',
                    message: (err: ValidationResultItem): string => {
                        return i18n('description-max-length', {
                            value: get(err, 'params.maxLength', '')
                        });
                    }
                },
                {
                    code: 'StringDefectIds.MUST_CONTAIN_ONLY_SYMBOLS',
                    message: (err: ValidationResultItem): string => {
                        return i18n('description-must-contain-only-symbols');
                    }
                }
            ]
        },
        {
            path: ['retargetingConditions', idx, 'rulesGroupsGeneral'],
            codes: [
                {
                    code: 'DefectIds.CANNOT_BE_NULL',
                    message: (err: ValidationResultItem): string => {
                        return i18n('rules-groups-cannot-be-null');
                    }
                },
                {
                    code: 'DefectIds.INCONSISTENT_STATE_ALREADY_EXISTS',
                    message: (err: ValidationResultItem): string => {
                        return i18n('inconsistent-rules-groups');
                    }
                }
            ]
        },
        {
            path: [
                'retargetingConditions',
                idx,
                'rulesGroups',
                idx,
                'items',
                idx
            ],
            codes: [
                {
                    code:
                        'RetargetingDefectIds.Gen.REQUIRED_TIME_FOR_GOAL_OR_SEGMENT',
                    message: (err: ValidationResultItem): string => {
                        return i18n('required-goal-period');
                    }
                }
            ]
        },
        {
            path: [
                'retargetingConditions',
                idx,
                'rulesGroups',
                idx,
                'items',
                idx,
                'id'
            ],
            codes: [
                {
                    code: 'DefectIds.CANNOT_BE_NULL',
                    message: (err: ValidationResultItem): string => {
                        return i18n('required-goal-id');
                    }
                },
                {
                    code: 'DefectIds.MUST_BE_VALID_ID',
                    message: (err: ValidationResultItem): string => {
                        return i18n('incorrect-goal-id');
                    }
                },
                {
                    code: 'DefectIds.OBJECT_NOT_FOUND',
                    message: (err: ValidationResultItem): string => {
                        return i18n('incorrect-goal-id');
                    }
                },
                {
                    code: 'RetargetingDefectIds.Gen.UNSUPPORTED_GOAL_ID',
                    message: (err: ValidationResultItem): string => {
                        return i18n('incorrect-goal-id');
                    }
                }
            ]
        },
        {
            path: [
                'retargetingConditions',
                idx,
                'rulesGroups',
                idx,
                'items',
                idx,
                'period'
            ],
            codes: [
                {
                    code: 'NumberDefectIds.MUST_BE_IN_THE_INTERVAL_INCLUSIVE',
                    message: (err: ValidationResultItem): string => {
                        return i18n('condition-rule-period-value-interval', {
                            minValue: get(err, 'params.min', ''),
                            maxValue: get(err, 'params.max', '')
                        });
                    }
                }
            ]
        }
    ],
    (): string => i18n('unknown-error')
);

const AUDIENCE_DOMAIN = 'audience.yandex.ru';

export function getDomainsFromRulesGroups(
    rulesGroups: Array<RetargetingConditionDataRulesGroup>,
    goals: Array<RetargetingConditionGoal>
): Array<string> {
    const allGoalsIds = rulesGroups.reduce(
        (
            res: Array<string>,
            rulesGroup: RetargetingConditionDataRulesGroup
        ) => {
            rulesGroup.items.forEach(item => {
                if (item.id) {
                    res.push(item.id);
                }
            });

            return res;
        },
        []
    );

    const domains = allGoalsIds.reduce((res: Array<string>, goalId: string) => {
        const goalData = goals.find(goal => goal.id === goalId);

        if (goalData) {
            if (goalData.type === GdGoalType.AUDIENCE) {
                res.push(AUDIENCE_DOMAIN);
            } else if (goalData.domain) {
                res.push(goalData.domain);
            }
        }

        return res;
    }, []);

    return uniq(domains);
}

export function getGoalsFromRulesGroups(
    rulesGroups: Array<RetargetingConditionDataRulesGroup>,
    goals: Array<RetargetingConditionGoal>
): Record<string, RetargetingConditionGoal> {
    const result = {};

    const allGoalsIds = rulesGroups.reduce(
        (
            res: Array<string>,
            rulesGroup: RetargetingConditionDataRulesGroup
        ) => {
            rulesGroup.items.forEach(item => {
                if (item.id) {
                    res.push(item.id);
                }
            });

            return res;
        },
        []
    );

    allGoalsIds.forEach((goalId: string) => {
        const goalData = goals.find(goal => goal.id === goalId);

        if (goalData) {
            result[goalData.id] = goalData;
        }
    });

    return result;
}

export function getAudienceTypeName(
    subtype: GoalSubtype | null,
    uploadingSourceId: string | null
): string | undefined {
    if (subtype === null) {
        return undefined;
    }

    if (subtype === GoalSubtype.UPLOADING_YUID) {
        if (uploadingSourceId === MEDIANA_SOURCE_ID) {
            return i18n('median');
        }

        return i18n('yandex-data');
    }

    const audienceDict = {
        [GoalSubtype.UPLOADING_EMAIL]: i18n('email'),
        [GoalSubtype.UPLOADING_PHONE]: i18n('phones'),
        [GoalSubtype.UPLOADING_MAC]: i18n('mac'),
        [GoalSubtype.UPLOADING_CRM]: i18n('crm'),
        [GoalSubtype.UPLOADING_IDFA_GAID]: i18n('mobiles-ids'),
        [GoalSubtype.LOOKALIKE]: i18n('look-alike'),
        [GoalSubtype.GEO_LAST]: i18n('geo-now'),
        [GoalSubtype.GEO_REGULAR]: i18n('geo-regular'),
        [GoalSubtype.GEO_CONDITION]: i18n('geo-condition'),
        [GoalSubtype.COMPOSITION_UNION]: i18n('composition'),
        [GoalSubtype.COMPOSITION_INTERSECT]: i18n('composition'),
        [GoalSubtype.COMPOSITION_SUBTRACT]: i18n('composition'),
        [GoalSubtype.METRIKA_COUNTER_ID]: i18n('metrika-segment'),
        [GoalSubtype.METRIKA_GOAL_ID]: i18n('metrika-segment'),
        [GoalSubtype.METRIKA_SEGMENT_ID]: i18n('metrika-segment'),
        [GoalSubtype.APPMETRICA_API_KEY]: i18n('app-metrika-segment'),
        [GoalSubtype.APPMETRICA_SEGMENT_ID]: i18n('app-metrika-segment'),
        [GoalSubtype.PIXEL]: i18n('pixel'),
        [GoalSubtype.UPLOADING_CLIENT_ID]: i18n('metrika-segment'),
        [GoalSubtype.GEO_HOME]: i18n('geo-regular'),
        [GoalSubtype.GEO_WORK]: i18n('geo-regular')
    };

    return audienceDict[subtype];
}
